import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { alphabet } from '../data/arabic';
import { speak, playClickSound } from '../utils/speech';

interface Props { onBack: () => void; age: 'little' | 'big'; }

export const AlphabetLesson: React.FC<Props> = ({ onBack, age }) => {
  const [current, setCurrent] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const letter = alphabet[current];

  // Auto-speak on card change
  useEffect(() => {
    const t = setTimeout(() => speak(letter.letter + '... ' + letter.word, 'ar', 0.7), 600);
    return () => clearTimeout(t);
  }, [current, letter]);

  const handleFlip = () => {
    playClickSound();
    setFlipped(!flipped);
    if (!flipped) speak(letter.word, 'ar');
  };

  const handleSpeakLetter = () => speak(letter.letter + '... ' + letter.name, 'ar', 0.7);

  return (
    <div style={{ minHeight: '100vh', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: '600px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
          <motion.button whileTap={{ scale: 0.9 }} onClick={onBack}
            style={{ background: 'white', border: '3px solid #FF6B6B', borderRadius: '50px', padding: '10px 20px', cursor: 'pointer', color: '#FF6B6B', fontWeight: 700 }}>
            ← Back
          </motion.button>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", color: 'var(--primary)', fontSize: '1.8rem' }}>
            Arabic Alphabet 🔤
          </h2>
        </div>

        <div style={{ background: '#F0F0F0', borderRadius: '20px', height: '12px', marginBottom: '10px', overflow: 'hidden' }}>
          <motion.div animate={{ width: `${((current + 1) / alphabet.length) * 100}%` }}
            style={{ height: '100%', background: 'linear-gradient(90deg, #FF6B6B, #6C63FF)', borderRadius: '20px' }} />
        </div>
        <p style={{ textAlign: 'center', color: '#636E72', marginBottom: '20px', marginTop: '5px' }}>
          {current + 1} of {alphabet.length}
        </p>

        <AnimatePresence mode="wait">
          <motion.div key={current} initial={{ x: 200, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
            exit={{ x: -200, opacity: 0 }} transition={{ type: 'spring', duration: 0.5 }}
            onClick={handleFlip}
            style={{
              background: flipped ? '#6C63FF' : 'white', borderRadius: '32px',
              padding: '50px 30px', textAlign: 'center', cursor: 'pointer',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', marginBottom: '20px',
              minHeight: '280px', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', transition: 'background 0.3s'
            }}>
            {!flipped ? (
              <>
                <div style={{ fontSize: '120px', lineHeight: 1.1, direction: 'rtl', marginBottom: '20px' }}>
                  {letter.letter}
                </div>
                <div style={{ fontSize: '1.5rem', color: '#636E72' }}>{letter.name}</div>
                <div style={{ fontSize: '0.9rem', color: '#B2BEC3', marginTop: '10px' }}>Tap to see the word! 👆</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '70px', marginBottom: '15px' }}>{letter.emoji}</div>
                <div style={{ fontSize: '2.5rem', color: 'white', direction: 'rtl', marginBottom: '10px' }}>{letter.word}</div>
                <div style={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.8)' }}>{letter.wordEn}</div>
                {age === 'big' && (
                  <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.6)', marginTop: '8px' }}>
                    Sound: /{letter.sound}/
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Voice button */}
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.9 }}
          onClick={handleSpeakLetter}
          style={{ display: 'block', margin: '0 auto 20px', background: 'linear-gradient(135deg, #FF6B6B, #FF9F43)', border: 'none', borderRadius: '50px', padding: '12px 30px', color: 'white', fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 15px rgba(255,107,107,0.4)' }}>
          🔊 Hear it!
        </motion.button>

        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
          <motion.button whileTap={{ scale: 0.9 }} onClick={() => { playClickSound(); setCurrent(Math.max(0, current - 1)); setFlipped(false); }}
            disabled={current === 0}
            style={{ background: current === 0 ? '#DDD' : 'white', border: '3px solid', borderColor: current === 0 ? '#DDD' : '#FF6B6B', borderRadius: '50px', padding: '12px 30px', cursor: current === 0 ? 'default' : 'pointer', color: current === 0 ? '#999' : '#FF6B6B', fontWeight: 700, fontSize: '1rem' }}>
            ← Prev
          </motion.button>
          <motion.button whileTap={{ scale: 0.9 }} onClick={() => { playClickSound(); setCurrent(Math.min(alphabet.length - 1, current + 1)); setFlipped(false); }}
            disabled={current === alphabet.length - 1}
            style={{ background: current === alphabet.length - 1 ? '#DDD' : '#FF6B6B', border: 'none', borderRadius: '50px', padding: '12px 30px', cursor: current === alphabet.length - 1 ? 'default' : 'pointer', color: 'white', fontWeight: 700, fontSize: '1rem' }}>
            Next →
          </motion.button>
        </div>
      </div>
    </div>
  );
};
